import verticalConfigJson from "./vertical_config.json";

export const VERTICALS = ["vineyard", "orchard", "greenhouse"] as const;
export type VerticalType = (typeof VERTICALS)[number];

type ThemeConfig = {
  primary: string;
  accent: string;
  background: string;
} | null;

type VerticalConfig = {
  labels: Record<string, string>;
  theme: ThemeConfig;
  features: string[];
  branding: {
    product_name: string;
    welcome_title: string;
  };
  assets?: {
    logo?: string;
    landing?: {
      hero_video?: string;
      detail_video?: string;
      hero_image?: string;
      detail_image?: string;
      consulting_bg?: string;
      about_bg?: string;
    };
  };
};

export const VERTICAL_CONFIG = verticalConfigJson as Record<VerticalType, VerticalConfig>;

export function normalizeVertical(value?: string | null): VerticalType {
  if (!value) return "vineyard";
  const lowered = value.toLowerCase();
  if (VERTICALS.includes(lowered as VerticalType)) {
    return lowered as VerticalType;
  }
  return "vineyard";
}

export function buildVerticalPath(vertical: VerticalType, path: string): string {
  const normalizedPath = path.startsWith("/") ? path : `/${path}`;
  if (vertical === "vineyard") {
    return normalizedPath;
  }
  return `/${vertical}${normalizedPath}`;
}

export function applyVerticalTheme(vertical: VerticalType): void {
  const root = document.documentElement;
  root.style.setProperty("--vertical-id", vertical);
  const theme = VERTICAL_CONFIG[vertical].theme;
  if (!theme) {
    root.style.removeProperty("--vertical-primary");
    root.style.removeProperty("--vertical-accent");
    root.style.removeProperty("--vertical-background");
    return;
  }

  root.style.setProperty("--vertical-primary", theme.primary);
  root.style.setProperty("--vertical-accent", theme.accent);
  root.style.setProperty("--vertical-background", theme.background);
}

export function getVerticalBranding(vertical: VerticalType): { brandName: string; logoSrc: string } {
  const config = VERTICAL_CONFIG[vertical];
  return {
    brandName: config.branding.product_name,
    logoSrc: config.assets?.logo || "/vineyard-logo.png",
  };
}

/**
 * Labels for app navigation (sidebar/menu) so wording matches vertical (vineyard / orchard / greenhouse).
 * - entityName: "Vineyard" | "Orchard" | "Greenhouse" (for Setup link and management group)
 * - unitLabel: "Block" | "Zone" (singular)
 * - unitLabelPlural: "Blocks" | "Zones"
 */
export function getVerticalNavLabels(vertical: VerticalType): {
  entityName: string;
  unitLabel: string;
  unitLabelPlural: string;
  managementGroupName: string;
} {
  const config = VERTICAL_CONFIG[vertical];
  const entityName = config.labels?.domain_entity ?? "Vineyard";
  const unitLabel = config.labels?.production_unit ?? "Block";
  const unitLabelPlural = unitLabel === "Zone" ? "Zones" : "Blocks";
  return {
    entityName,
    unitLabel,
    unitLabelPlural,
    managementGroupName: `${entityName} Management`,
  };
}

export function getVerticalLandingAssets(vertical: VerticalType): NonNullable<VerticalConfig["assets"]>["landing"] {
  return VERTICAL_CONFIG[vertical].assets?.landing || {};
}

/**
 * Centralized container/border/UI styles per vertical. Use in both public pages and
 * authenticated app (Layout) so borders and accents stay consistent and DRY.
 */
export interface VerticalAppStyles {
  accentBorder: string;
  accentBorderLeft: string;
  inputBorder: string;
  formCardBorder: string;
  sidebarBorder: string;
  footerBorder: string;
  footerBg: string;
  mainBg: string;
  navActiveBg: string;
  navActiveBorder: string;
  navActiveText: string;
  navActiveDot: string;
  headerBarClass: string;
  linkAccent: string;
  /** Primary CTA (e.g. Get Started). Vineyard: leaf gradient; greenhouse: solid green; orchard: solid blue. */
  ctaPrimary: string;
  /** Secondary CTA (e.g. Sign in, Back to Home). Vineyard: grape gradient; greenhouse: outline green; orchard: outline blue. */
  ctaSecondary: string;
}

export function getVerticalAppStyles(vertical: VerticalType): VerticalAppStyles {
  switch (vertical) {
    case "greenhouse":
      return {
        accentBorder: "border-green-200",
        accentBorderLeft: "border-l-green-600",
        inputBorder: "border-green-300",
        formCardBorder: "border-green-200",
        sidebarBorder: "border-green-200",
        footerBorder: "border-green-200",
        footerBg: "bg-green-50",
        mainBg: "bg-green-50",
        navActiveBg: "bg-green-300",
        navActiveBorder: "border-l-4 border-green-600",
        navActiveText: "text-green-950",
        navActiveDot: "text-green-600",
        headerBarClass: "border-b-2 border-green-950 bg-gradient-to-r from-green-900 to-green-700",
        linkAccent: "text-green-600 hover:text-green-700",
        ctaPrimary: "inline-flex items-center justify-center px-8 py-4 border border-transparent text-base font-semibold rounded text-white bg-green-600 hover:bg-green-700 shadow-lg hover:shadow-xl transition-all duration-300",
        ctaSecondary: "inline-flex items-center justify-center px-8 py-4 border-2 border-green-600 text-green-700 bg-white hover:bg-green-50 font-semibold rounded shadow-lg hover:shadow-xl transition-all duration-300",
      };
    case "orchard":
      return {
        accentBorder: "border-blue-200",
        accentBorderLeft: "border-l-blue-600",
        inputBorder: "border-blue-300",
        formCardBorder: "border-blue-200",
        sidebarBorder: "border-blue-200",
        footerBorder: "border-blue-200",
        footerBg: "bg-blue-50",
        mainBg: "bg-blue-50",
        navActiveBg: "bg-blue-300",
        navActiveBorder: "border-l-4 border-blue-600",
        navActiveText: "text-blue-950",
        navActiveDot: "text-blue-600",
        headerBarClass: "border-b-2 border-blue-950 bg-gradient-to-r from-blue-900 to-blue-700",
        linkAccent: "text-blue-600 hover:text-blue-700",
        ctaPrimary: "inline-flex items-center justify-center px-8 py-4 border border-transparent text-base font-semibold rounded text-white bg-blue-600 hover:bg-blue-700 shadow-lg hover:shadow-xl transition-all duration-300",
        ctaSecondary: "inline-flex items-center justify-center px-8 py-4 border-2 border-blue-600 text-blue-700 bg-white hover:bg-blue-50 font-semibold rounded shadow-lg hover:shadow-xl transition-all duration-300",
      };
    default:
      return {
        accentBorder: "border-grape-200",
        accentBorderLeft: "border-l-grape-600",
        inputBorder: "border-wood-300",
        formCardBorder: "border-grape-700",
        sidebarBorder: "border-wood-300",
        footerBorder: "border-wood-300",
        footerBg: "bg-wood-50",
        mainBg: "bg-vineyard-light",
        navActiveBg: "bg-grape-300",
        navActiveBorder: "border-l-4 border-grape-600",
        navActiveText: "text-grape-950",
        navActiveDot: "text-grape-600",
        headerBarClass: "border-b-2 border-wood-950 wood-texture",
        linkAccent: "text-grape-600 hover:text-grape-700",
        ctaPrimary: "inline-flex items-center justify-center px-8 py-4 border border-transparent text-base font-semibold rounded text-vineyard-white bg-gradient-to-br from-leaf-400 to-leaf-600 hover:from-leaf-500 hover:to-leaf-700 shadow-lg hover:shadow-xl transition-all duration-300",
        ctaSecondary: "inline-flex items-center justify-center px-8 py-4 border border-transparent text-base font-semibold rounded text-vineyard-white bg-gradient-to-br from-grape-400 to-grape-600 hover:from-grape-500 hover:to-grape-700 shadow-lg hover:shadow-xl transition-all duration-300",
      };
  }
}
